import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Award, Wrench, Building, RefreshCw, AlertTriangle, ShieldCheck } from "lucide-react";
import { Reveal, MaskedLine, StaggerGroup, StaggerItem } from "./motion/Reveal";

const OWNER_PHOTO = "https://customer-assets.emergentagent.com/job_ataiwo-elektricien/artifacts/wyz6ub5l_IMG_4675.webp";
const EXPERTISE_ICONS = [Award, Building, Wrench, RefreshCw, AlertTriangle, ShieldCheck];
const EXPERTISE_KEYS = ["years", "residential", "utility", "renovation", "faults", "nen"];

export default function About() {
  const { t } = useTranslation();

  return (
    <section id="over-ons" data-testid="about-section" className="section bg-[#F4F4F5] relative overflow-hidden">
      <div
        aria-hidden
        className="absolute -top-40 -right-40 w-96 h-96 bg-[#FF5A1F]/[0.04] rounded-full blur-3xl"
      />
      <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center relative">
        <div className="lg:col-span-7 order-2 lg:order-1">
          <Reveal>
            <div className="flex items-center gap-3 mb-4">
              <span className="accent-bar" />
              <span className="text-[#FF5A1F] uppercase font-bold tracking-widest text-xs">
                {t("about.eyebrow")}
              </span>
            </div>
          </Reveal>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#0A1128] mb-6 leading-[1.02]">
            <span className="block overflow-hidden"><MaskedLine text={t("about.title_1")} wordDelay={0.06} /></span>
            <span className="block overflow-hidden"><MaskedLine text={t("about.title_2")} wordDelay={0.05} delay={0.15} /></span>
          </h2>
          <Reveal delay={0.15}>
            <p className="text-base md:text-lg text-slate-700 leading-relaxed mb-5">
              {t("about.body_1")}
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-base md:text-lg text-slate-700 leading-relaxed mb-8">
              {t("about.body_2")}
            </p>
          </Reveal>

          <StaggerGroup className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {EXPERTISE_KEYS.map((k, i) => {
              const Icon = EXPERTISE_ICONS[i];
              return (
                <StaggerItem key={k}>
                  <div className="flex items-center gap-3 bg-white border border-slate-200 p-3.5 hover:border-[#FF5A1F] transition-colors">
                    <span className="w-9 h-9 shrink-0 bg-[#0A1128] flex items-center justify-center">
                      <Icon className="w-4 h-4 text-[#FF5A1F]" strokeWidth={2.4} />
                    </span>
                    <span className="font-bold text-[#0A1128] text-sm md:text-[15px]">
                      {t(`about.expertise.${k}`)}
                    </span>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>

        <div className="lg:col-span-5 order-1 lg:order-2 relative">
          <Reveal y={60}>
            <div className="relative group">
              {/* Photo with subtle hover zoom inside a clipped editorial frame */}
              <div className="relative aspect-[4/5] bg-[#0A1128] overflow-hidden border border-slate-800">
                <img
                  src={OWNER_PHOTO}
                  alt={t("about.photo_alt")}
                  className="absolute inset-0 w-full h-full object-cover object-[65%_50%] transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                  loading="lazy"
                  decoding="async"
                  width="800"
                  height="1000"
                  style={{ transform: "translateZ(0)", backfaceVisibility: "hidden" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/70 via-transparent to-transparent pointer-events-none" />
                {/* Corner accents */}
                <div className="absolute top-3 left-3 w-6 h-6 border-l-2 border-t-2 border-[#FF5A1F]" />
                <div className="absolute top-3 right-3 w-6 h-6 border-r-2 border-t-2 border-[#FF5A1F]" />
                <div className="absolute bottom-3 left-3 w-6 h-6 border-l-2 border-b-2 border-[#FF5A1F]" />
                <div className="absolute bottom-3 right-3 w-6 h-6 border-r-2 border-b-2 border-[#FF5A1F]" />
                {/* Editorial photo caption */}
                <div className="absolute top-4 right-4 [writing-mode:vertical-rl] rotate-180 text-white/60 text-[10px] uppercase tracking-[0.3em] font-bold pointer-events-none">
                  Fig. 01 — Ataiwo · 2026
                </div>
              </div>

              {/* Owner name plate */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.35 }}
                className="absolute -bottom-6 -left-4 md:-left-8 bg-[#FF5A1F] text-white p-5 md:p-6 max-w-[85%] shadow-[8px_8px_0_0_#0A1128]"
              >
                <div className="font-display text-2xl md:text-3xl leading-tight mb-1">Abeeb Taiwo</div>
                <div className="text-xs md:text-sm font-bold uppercase tracking-wider text-white/95">
                  {t("about.owner_role")}
                </div>
              </motion.div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
